import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'
import Page from './Page'
import {
  createCoverTexture,
  createPageTexture,
  createImagePageTexture,
  createTextPageTexture,
} from './BookTextures'
import { BOOK_CONTENT } from './bookContent'

/**
 * Book — クリックでページをめくれる本。
 * Wawa Sensei「3D Book Slider」（r3f-animated-book-slider）のバニラ Three.js 移植。
 *
 * ページの内容（表紙タイトル・各面の画像/文字）は bookContent.js で定義する。
 * 画像を追加する場合は static/image/book/ に置き、sources.js に texture として登録した上で
 * bookContent.js の face に { type: 'image', source: '<sources.jsのname>' } を書く。
 */

// 中ページの配色バリエーション（めくった時の変化が分かりやすいように）
const PAGE_COLORS = [
  { bgColor: '#f5efe0', accentColor: '#8a7a5c' },
  { bgColor: '#e8f0e4', accentColor: '#5c7a5e' },
  { bgColor: '#e4ecf4', accentColor: '#5c6e8a' },
  { bgColor: '#f4e8e4', accentColor: '#8a5c5c' },
]

export default class Book {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.debug = this.experience.debug

    // page: 目標ページ / delayedPage: 実際にめくれた枚数（多ページジャンプは1枚ずつ波状に追従）
    this.page = 0
    this.delayedPage = 0
    this.bookClosed = true
    this.turnTimeout = null
    // 1ページめくるのにかける時間（秒）。ページの回転もカールもこれに同期する
    this.turnDuration = 1.8
    // めくり中の紙の変形量: turning = カール（アーチ）強さ / foldDeg = 外側の折れ角（度）
    this.curlParams = { turning: 0.105, foldDeg: 1 }
    // true でページを曲げず、硬い板のように平らなまま開閉する（curlParams は無視される）
    this.flatMode = true

    this.setPages()
    this.setGroups()
    this.setDebug()
  }

  setPages() {
    const resources = this.experience.resources

    // 面番号（1〜10）→ テクスチャ。内容は bookContent.js の faces で指定し、
    // 未定義の面は番号入りプレースホルダーになる
    const faceTexture = (faceNumber) => {
      const def = BOOK_CONTENT.faces[faceNumber - 1]
      const colors = PAGE_COLORS[(faceNumber - 1) % PAGE_COLORS.length]

      if (def?.type === 'image') {
        return createImagePageTexture({ image: resources.items[def.source].image, ...colors })
      }
      if (def?.type === 'text') {
        return createTextPageTexture({ title: def.title, items: def.items, ...colors })
      }
      return createPageTexture({ label: String(faceNumber), ...colors })
    }

    // 表紙 + 中4枚 + 裏表紙 = 6枚（1枚が表裏2面を持つ）。面番号は 1〜10
    const sheets = [
      { front: createCoverTexture({ ...BOOK_CONTENT.cover }), back: faceTexture(1) },
    ]
    for (let i = 0; i < 4; i++) {
      sheets.push({ front: faceTexture(2 + i * 2), back: faceTexture(3 + i * 2) })
    }
    sheets.push({
      front: faceTexture(10),
      back: createCoverTexture({ bgColor: '#3a2c45', ...BOOK_CONTENT.backCover }),
    })

    this.pages = sheets.map((sheet, index) =>
      new Page({ book: this, number: index, frontTexture: sheet.front, backTexture: sheet.back })
    )
  }

  setGroups() {
    // group（ワールド配置）→ floatGroup（チルト+浮遊）→ bookGroup（本の基本向き）→ 各ページ
    this.group = new THREE.Group()
    this.floatGroup = new THREE.Group()
    this.bookGroup = new THREE.Group()

    this.baseTilt = -Math.PI / 4
    this.floatGroup.rotation.x = this.baseTilt
    this.bookGroup.rotation.y = -Math.PI / 2
    this.floatParams = { amplitude: 0.1, speed: 0.3 }

    this.pages.forEach((page) => this.bookGroup.add(page.group))
    this.floatGroup.add(this.bookGroup)
    this.group.add(this.floatGroup)

    // 初期配置: 焚き火のそば・キャラクターの右（#debug の GUI で調整して焼き戻す）
    this.group.position.set(-3.3, -1.45, 18.4)
    this.group.rotation.y = 0.5084
    this.group.scale.setScalar(0.25)

    this.scene.add(this.group)
  }

  /**
   * @param {number} target めくり先のページ（0 = 閉じた表紙）
   * @param {{ byUser?: boolean }} options byUser はクリック等ユーザー操作起点のとき true。
   *   その場合のみ 'bookPageChanged' を発火して 2D UI 側（PortfolioUI）が追従スクロールする。
   *   SceneDirector からのプログラム的な呼び出しでは発火しない（スクロール→開く→スクロールのループ防止）
   */
  setPage(target, { byUser = false } = {}) {
    const clamped = Math.max(0, Math.min(this.pages.length, Math.round(target)))
    if (clamped === this.page) return

    // 表紙・裏表紙の開閉（閉じた状態 ↔ 隣のページ）は表紙用の音、ページ間の移動はページ用の音
    const coverTurn =
      this.page === 0 || clamped === 0 ||
      this.page === this.pages.length || clamped === this.pages.length
    this.experience.audioManager?.playSE(coverTurn ? 'bookPageTurnSE' : 'bookPageTurn2SE', 0.6)

    this.page = clamped

    if (byUser) {
      document.dispatchEvent(new CustomEvent('bookPageChanged', { detail: { page: this.page } }))
    }

    clearTimeout(this.turnTimeout)
    this.stepTowardPage()
  }

  // delayedPage を目標へ±1ずつ進める。残距離が大きいうちは速く、着地は緩やかに
  stepTowardPage() {
    if (this.delayedPage === this.page) return

    const remaining = Math.abs(this.page - this.delayedPage)
    this.delayedPage += Math.sign(this.page - this.delayedPage)
    this.applyPageState()

    if (this.delayedPage !== this.page) {
      // めくり時間に比例した間隔で次のページへ（前のめくりがある程度進んでから始まる）
      const interval = this.turnDuration * 1000 * (remaining > 2 ? 0.15 : 0.35)
      this.turnTimeout = setTimeout(() => this.stepTowardPage(), interval)
    }
  }

  applyPageState() {
    this.bookClosed = this.delayedPage === 0 || this.delayedPage === this.pages.length
    this.pages.forEach((page, index) => {
      page.opened = this.delayedPage > index
    })
  }

  /**
   * 開いた見開きへカメラを寄せるための姿勢を返す。
   * 見開きの法線 = bookGroup ローカル +x（開いたページの見える面の向き）。
   * ワールド変換から毎回算出するので、本を動かしても座標のベタ書き不要。
   * @param {number} distance 見開き中心からカメラまでの距離（ワールド単位）
   * @returns {{ cameraPos: THREE.Vector3, lookTarget: THREE.Vector3 }}
   */
  getFocusPose(distance = 1.2) {
    this.group.updateWorldMatrix(true, true)
    const lookTarget = this.bookGroup.getWorldPosition(new THREE.Vector3())
    const quaternion = this.bookGroup.getWorldQuaternion(new THREE.Quaternion())
    const normal = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion).normalize()
    return {
      cameraPos: lookTarget.clone().addScaledVector(normal, distance),
      lookTarget,
    }
  }

  /**
   * カメラフォーカス中はページが読めるよう浮遊揺れを抑える（解除で元に戻す）。
   */
  setFloatCalm(calm) {
    gsap.to(this.floatParams, {
      amplitude: calm ? 0.02 : 0.1,
      duration: 1.0,
      ease: 'power2.inOut',
      overwrite: true,
    })
  }

  update() {
    // Time.delta は ms。秒に変換し、タブ復帰時のスパイクをクランプ
    const dt = Math.min(this.time.delta, 100) / 1000
    const elapsed = this.time.elapsed

    // ゆったりとした浮遊（drei <Float> の簡易版）
    this.floatGroup.rotation.x =
      this.baseTilt + Math.sin(elapsed * this.floatParams.speed) * this.floatParams.amplitude
    this.floatGroup.position.y = Math.sin(elapsed * this.floatParams.speed * 1.6) * 0.05

    this.pages.forEach((page) => page.update(dt))
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Book')
    this.debugFolder.add(this.group.position, 'x').min(-15).max(15).step(0.05).name('Position X')
    this.debugFolder.add(this.group.position, 'y').min(-10).max(10).step(0.05).name('Position Y')
    this.debugFolder.add(this.group.position, 'z').min(0).max(28).step(0.05).name('Position Z')
    this.debugFolder.add(this.group.rotation, 'y').min(-Math.PI).max(Math.PI).step(0.01).name('Rotation Y')
    this.debugFolder.add(this.group.scale, 'x').min(0.1).max(3).step(0.05).name('Scale')
      .onChange((value) => this.group.scale.setScalar(value))
    this.debugFolder.add(this, 'flatMode').name('Flat Mode')
    this.debugFolder.add(this, 'turnDuration').min(0.3).max(3).step(0.1).name('Turn Duration (s)')
    this.debugFolder.add(this.curlParams, 'turning').min(0).max(0.3).step(0.005).name('Curl Strength')
    this.debugFolder.add(this.curlParams, 'foldDeg').min(0).max(3).step(0.1).name('Fold Angle (deg)')
    this.debugFolder.add(this.floatParams, 'amplitude').min(0).max(0.3).step(0.01).name('Float Amplitude')
    this.debugFolder.add(this.floatParams, 'speed').min(0).max(2).step(0.05).name('Float Speed')

    this.debugFolder.add({ next: () => this.setPage(this.page + 1) }, 'next').name('📖 Open Next')
    this.debugFolder.add({ prev: () => this.setPage(this.page - 1) }, 'prev').name('📖 Prev')
    this.debugFolder.add({ close: () => this.setPage(0) }, 'close').name('📕 Close (cover)')
    this.debugFolder.add({ all: () => this.setPage(this.pages.length) }, 'all').name('📚 Open All (back)')
    this.debugFolder.close()
  }
}
